// @ts-nocheck
// Location: src/components/admin/ContentMachine.tsx
// Purpose: Content machine management tools for creating and managing content machines
// Scope: Shows content machines and their history in a searchable and filterable interface

/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Id } from '@/convex/_generated/dataModel';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SlideSelector } from '@/components/ui/slide-selector';
import { cn } from '@/lib/utils';
import { useConfetti } from '@/hooks/useConfetti';
import { useNavigate } from 'react-router-dom';
import { LinkedInIcon } from '@/components/ui/icons/linkedin-icon';
import { Badge } from '@/components/ui/badge';
import { MiniPostAnalytics } from '@/components/shared/MiniPostAnalytics';
import { Loader, LOADER_MESSAGES } from '@/components/ui/loader';

interface ContentMachine {
	_id: Id<'contentMachine'>;
	name: string;
	platform: string;
	userId: Id<'users'>;
	profileId?: Id<'profiles'>;
	profileKey?: string;
	content?: string;
	status?: string;
	type?: string;
	promptName?: string;
	promptText?: string;
	formattedPrompt?: string;
	scheduledFor?: string;
	publishedAt?: string;
	createdAt: string;
	updatedAt: string;
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
	metadata?: {
		engagement?: {
			comments?: number;
			likes?: number;
			shares?: number;
			impressions?: number;
		};
		tags?: string[];
		topics?: string[];
		sentiment?: string;
		language?: string;
		lastGeneratedAt?: string;
	};
	error?: string;
}

interface RssFeed {
	_id: Id<'rssFeeds'>;
	title: string;
	url: string;
	userId: Id<'users'>;
	isActive: boolean;
	lastFetched?: string;
	lastError?: string;
	newItemsCount?: number;
	createdAt: string;
	updatedAt: string;
}

interface BuzzAgent {
	_id: Id<'buzzAgents'>;
	name: string;
	isActive: boolean;
	isPublic: boolean;
	totalItems?: number;
	publicItems?: number;
	metadata?: {
		description?: string;
	};
}

interface ContentHistory {
	_id: Id<'contentHistory'>;
	machineId: Id<'contentMachine'>;
	content: string;
	status: string;
	createdAt: string;
	approvedAt?: string;
	platform: string;
	profileId?: Id<'profiles'>;
	brandVoice?: {
		id: string;
		profileId?: Id<'profiles'>;
	};
	metadata?: {
		buzzUrl?: string;
		templateName?: string;
		postUrl?: string;
		postId?: string;
		publishedAt?: string;
		engagement?: {
			likes?: number;
			comments?: number;
			shares?: number;
			views?: number;
		};
	};
}

interface User {
	_id: Id<'users'>;
	email: string;
	name: string;
	isAdmin?: boolean;
}

interface Profile {
	_id: Id<'profiles'>;
	profileTitle: string;
	email: string;
	userId?: Id<'users'>;
	profileKey: string;
	writingAnalysis?: string;
	linkStatus?: {
		status: string;
		platform: string;
	};
}

function BrandVoicePill({ brandVoice }: { brandVoice?: { id: string } }) {
	if (!brandVoice) return null;

	const getBrandVoiceInfo = (id: string) => {
		switch (id) {
			case 'prometheus':
				return {
					label: 'Prometheus',
					emoji: '🔥',
					color: 'from-orange-400 to-orange-600',
				};
			case 'reverend':
				return {
					label: 'The Reverend',
					emoji: '🎭',
					color: 'from-purple-400 to-purple-600',
				};
			case 'brent':
				return {
					label: 'Brent Ecomm Master',
					emoji: '💼',
					color: 'from-blue-400 to-blue-600',
				};
			case 'user':
				return {
					label: 'My Brand Voice',
					emoji: '✨',
					color: 'from-emerald-400 to-emerald-600',
				};
			default:
				return {
					label: 'Unknown',
					emoji: '❓',
					color: 'from-gray-400 to-gray-600',
				};
		}
	};

	const info = getBrandVoiceInfo(brandVoice.id);
	return (
		<span
			className={cn(
				'px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-b text-white',
				info.color
			)}
		>
			<span className='text-inherit'>{info.emoji}</span> {info.label}
		</span>
	);
}

export function ContentMachine() {
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();
	const [_searchTerm, setSearchTerm] = React.useState('');
	const [selectedPlatform, setSelectedPlatform] = React.useState('twitterx');
	const [error, setError] = React.useState<string | null>(null);
	const [newMachineName, setNewMachineName] = React.useState('');
	const [selectedBuzzAgentId, setSelectedBuzzAgentId] =
		React.useState<Id<'buzzAgents'> | null>(null);
	const [isCreating, setIsCreating] = React.useState(false);
	const { user, isAdmin, isLoading: authLoading } = useAuth();
	const navigate = useNavigate();
	const [hoveredCard, setHoveredCard] =
		React.useState<Id<'contentMachine'> | null>(null);
	const createContentMachineMutation = useMutation(api.contentMachine.create);
	const updateBrandVoiceMutation = useMutation(
		api.contentMachine.updateBrandVoice
	);

	const profiles = useQuery(
		api.profiles.list,
		user?.id && user?.initialApiKey
			? {
					userId: user.id as unknown as Id<'users'>,
					isSuperAdmin: isAdmin || false,
				}
			: 'skip'
	) as Profile[] | undefined;
	const users = useQuery(
		api.users.list,
		user?.id && user?.initialApiKey
			? {
					requestingUserId: user.id as unknown as string,
					apiKey: user.initialApiKey,
				}
			: 'skip'
	) as User[] | undefined;
	const contentMachines = useQuery(
		api.contentMachine.list,
		user?.id && user?.initialApiKey && !authLoading
			? {
					userId: null,
					requestingUserId: user.id as unknown as Id<'users'>,
				}
			: 'skip'
	) as ContentMachine[] | undefined;
	const contentHistory = useQuery(api.contentMachine.getAllContentHistory, {
		limit: 1000,
	}) as ContentHistory[] | undefined;
	const buzzAgents = useQuery(
		api.buzzAgents.list,
		user?.id
			? {
					userId: user.id as unknown as Id<'users'>,
					includePublic: true,
				}
			: 'skip'
	) as BuzzAgent[] | undefined;
	const rssFeeds = useQuery(api.rssFeeds.list) as RssFeed[] | undefined;
	const activeBuzzAgents = React.useMemo(() => {
		return [...(buzzAgents || [])]
			.filter((agent) => agent.isActive !== false)
			.sort((a, b) => {
				const aIsPrometheus = a.name.toLowerCase().includes('prometheus');
				const bIsPrometheus = b.name.toLowerCase().includes('prometheus');
				if (aIsPrometheus && !bIsPrometheus) return -1;
				if (!aIsPrometheus && bIsPrometheus) return 1;
				return a.name.localeCompare(b.name);
			});
	}, [buzzAgents]);
	const buzzAgentOptions = React.useMemo(() => {
		return activeBuzzAgents
			.map((agent) => ({
				value: agent._id,
				label: agent.name,
				data: agent,
				render: () => (
					<div className='flex w-full items-center justify-between gap-3'>
						<div className='flex min-w-0 flex-col'>
							<span className='truncate'>{agent.name}</span>
							<span className='text-xs text-muted-foreground'>
								{agent.totalItems ?? agent.publicItems ?? 0} ready topics
							</span>
						</div>
					</div>
				),
			}));
	}, [activeBuzzAgents]);
	const defaultBuzzAgentId = React.useMemo(() => {
		const prometheusAgent = activeBuzzAgents.find((agent) =>
			agent.name.toLowerCase().includes('prometheus')
		);
		return prometheusAgent?._id ?? activeBuzzAgents[0]?._id ?? null;
	}, [activeBuzzAgents]);
	React.useEffect(() => {
		if (!activeBuzzAgents.length) {
			if (selectedBuzzAgentId) setSelectedBuzzAgentId(null);
			return;
		}

		const selectedAgentExists = activeBuzzAgents.some(
			(agent) => agent._id === selectedBuzzAgentId
		);
		if ((!selectedBuzzAgentId || !selectedAgentExists) && defaultBuzzAgentId) {
			setSelectedBuzzAgentId(defaultBuzzAgentId);
		}
	}, [activeBuzzAgents, defaultBuzzAgentId, selectedBuzzAgentId]);
	const isBuzzAgentsLoading = buzzAgents === undefined;
	const hasActiveBuzzAgents = activeBuzzAgents.length > 0;
	const canCreateContentMachine =
		!isCreating &&
		Boolean(newMachineName.trim()) &&
		!isBuzzAgentsLoading &&
		hasActiveBuzzAgents &&
		Boolean(selectedBuzzAgentId);
	const rssFeedOptions = React.useMemo(() => {
		const titleCounts = new Map<string, number>();
		const visibleFeeds = (rssFeeds || []).filter(
			(feed) => feed.metadata?.source !== 'google_trends_rss'
		);
		for (const feed of visibleFeeds) {
			titleCounts.set(feed.title, (titleCounts.get(feed.title) || 0) + 1);
		}

		const feedStatus = (feed: RssFeed) => {
			if (feed.lastError) return 'source error';
			if (!feed.isActive) return 'inactive';
			if (feed.lastFetched) {
				const date = new Date(feed.lastFetched);
				if (!Number.isNaN(date.getTime())) {
					return `refreshed ${date.toLocaleDateString()}`;
				}
			}
			return 'active';
		};

		return [...visibleFeeds]
			.sort((a, b) => {
				const aIsTrends = a.title.toLowerCase().includes('google trends');
				const bIsTrends = b.title.toLowerCase().includes('google trends');
				if (aIsTrends && !bIsTrends) return -1;
				if (!aIsTrends && bIsTrends) return 1;
				return a.title.localeCompare(b.title);
			})
			.map((feed) => ({
				value: feed._id,
				label: `${feed.title} - ${feedStatus(feed)}${
					(titleCounts.get(feed.title) || 0) > 1
						? ` - ${String(feed._id).slice(-6)}`
						: ''
				}`,
				data: feed,
				render: () => (
					<div className='flex w-full items-center justify-between gap-3'>
						<div className='flex min-w-0 flex-col'>
							<span className='truncate'>{feed.title}</span>
							<span className='text-xs text-muted-foreground'>
								{feedStatus(feed)}
								{feed.newItemsCount !== undefined
									? ` · ${feed.newItemsCount} ready`
									: ''}
								{(titleCounts.get(feed.title) || 0) > 1
									? ` · ${String(feed._id).slice(-6)}`
									: ''}
							</span>
						</div>
					</div>
				),
			}));
	}, [rssFeeds]);

	const isLoading = React.useMemo(() => {
		if (!user?.id || !user?.initialApiKey) return true;
		return !contentMachines || !contentHistory || !users;
	}, [user?.id, user?.initialApiKey, contentMachines, contentHistory, users]);

	const getUserInfo = (userId: string) => {
		const user = users?.find((u: User) => u._id === userId);
		return {
			email: user?.email || 'Unknown',
			name: user?.name || 'Unknown User',
		};
	};

	// Group machines by creator
	const groupedMachines = React.useMemo(() => {
		if (isLoading || !users || !contentMachines || !user?.id) {
			return { userMachines: [], otherMachines: {} };
		}

		// First, separate user's machines
		const userMachines = contentMachines.filter(
			(m: ContentMachine) => m.userId?.toString() === user.id?.toString()
		);
		const otherMachines = contentMachines.filter(
			(m: ContentMachine) =>
				m.userId?.toString() !== user.id?.toString() && m.userId
		);

		// Create a map of user info first
		const userInfoMap = new Map(
			users.map((u: User) => [
				u._id.toString(),
				{
					email: u.email || 'Unknown',
					name: u.name || 'Unknown User',
				},
			])
		);

		// Group other machines by userId
		const grouped = otherMachines.reduce(
			(
				acc: Record<
					string,
					{ name: string; email: string; machines: ContentMachine[] }
				>,
				machine: ContentMachine
			) => {
				if (!machine.userId) return acc;
				const userInfo = userInfoMap.get(machine.userId.toString()) || {
					email: 'Unknown',
					name: 'Unknown User',
				};
				const key = userInfo.email;

				if (!acc[key]) {
					acc[key] = {
						name: userInfo.name,
						email: userInfo.email,
						machines: [],
					};
				}
				acc[key].machines.push(machine);
				return acc;
			},
			{}
		);

		return {
			userMachines,
			otherMachines: grouped,
		};
	}, [isLoading, contentMachines, users, user?.id]);

	const getMachineHistory = (machineId: Id<'contentMachine'>) => {
		return (
			contentHistory
				?.filter(
					(h) => h.machineId === machineId && h.metadata?.postUrl
				)
				?.sort(
					(a, b) =>
						new Date(b.createdAt).getTime() -
						new Date(a.createdAt).getTime()
				)
				?.slice(0, 3) || []
		);
	};

	// Add helper function to get latest published date
	const getLatestPublishedDate = (machineId: Id<'contentMachine'>) => {
		const history = contentHistory?.filter(
			(h) => h.machineId === machineId && h.metadata?.postUrl
		);
		if (!history || history.length === 0) return new Date(0);
		return new Date(
			Math.max(...history.map((h) => new Date(h.createdAt).getTime()))
		);
	};

	const renderMachinesList = () => {
		const { userMachines, otherMachines } = groupedMachines;
		if (!userMachines) return null;

		// Sort machines by platform and latest published date
		const sortedMachines = [...userMachines].sort((a, b) => {
			// First sort by platform to keep platforms grouped
			if (a.platform < b.platform) return -1;
			if (a.platform > b.platform) return 1;

			// Then sort by latest published date within each platform
			const dateA = getLatestPublishedDate(a._id);
			const dateB = getLatestPublishedDate(b._id);
			return dateB.getTime() - dateA.getTime();
		});

		return (
			<div className='space-y-6'>
				{/* User's Content Machines */}
				<Card className='mb-6 glow-card'>
					<CardHeader>
						<div className='space-y-2'>
							<div className='flex items-center gap-2'>
								<span className='text-lg font-semibold uppercase text-foreground'>
									MY CONTENT MACHINES:
								</span>
								<span className='text-2xl font-bold text-pink-500 uppercase'>
									{isLoading ? (
										<span className='animate-pulse'>
											LOADING...
										</span>
									) : (
										getUserInfo(
											user?.id as unknown as string
										).name
									)}
								</span>
							</div>
							<div className='text-sm text-muted-foreground'>
								{isLoading ? (
									<Loader
										className='py-12'
										message='LOADING CONTENT MACHINES...'
									/>
								) : (
									<>
										{sortedMachines.length} machine
										{sortedMachines.length !== 1
											? 's'
											: ''}{' '}
										• {user?.email}
									</>
								)}
							</div>
						</div>
					</CardHeader>
					<CardContent>
						{!isLoading && (
							<div className='space-y-8'>
								{/* Twitter X Machines */}
								{sortedMachines.filter(
									(m) => m.platform === 'twitterx'
								).length > 0 && (
									<div className='space-y-4'>
										<h3 className='text-3xl font-bold text-purple-500'>
											𝕏 TWITTER
										</h3>
										<div className='grid grid-cols-1 gap-4'>
											{sortedMachines
												.filter(
													(machine) =>
														machine.platform ===
														'twitterx'
												)
												.map((machine) => (
													<Card
														key={machine._id}
														className='bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors'
														onClick={() =>
															navigate(
																`/admin/content-machine/${machine._id}`
															)
														}
														onMouseEnter={() =>
															setHoveredCard(
																machine._id
															)
														}
														onMouseLeave={() =>
															setHoveredCard(null)
														}
													>
														<CardHeader className='pb-2'>
															<div className='flex flex-col gap-2'>
																<div className='flex items-center gap-2'>
																	<span className='text-xl px-6 py-3 bg-gradient-to-b from-zinc-800 via-zinc-900 to-black text-white rounded-full font-bold border border-zinc-700'>
																		𝕏
																		Content
																		Machine
																	</span>
																</div>
																<CardTitle className='text-lg whitespace-nowrap mt-2'>
																	{
																		machine.name
																	}
																</CardTitle>
															</div>
														</CardHeader>
														<CardContent>
															<div className='space-y-4'>
																{getMachineHistory(
																	machine._id
																).map(
																	(
																		history
																	) => (
																		<div
																			key={
																				history._id
																			}
																			className='flex flex-col gap-4 p-6 rounded-lg mb-6 bg-white/5 backdrop-blur-sm border border-white/10 shadow-xl'
																		>
																			<div className='flex items-center gap-2 text-lg text-gray-400'>
																				{history
																					.metadata
																					?.postUrl && (
																					<Badge
																						variant='outline'
																						className='bg-gradient-to-b from-pink-400 to-pink-600 text-white px-3 py-1'
																					>
																						🚀
																						Published
																					</Badge>
																				)}
																				<BrandVoicePill
																					brandVoice={
																						history.brandVoice
																					}
																				/>
																				<span className='text-gray-400'>
																					{new Date(
																						history.createdAt
																					).toLocaleDateString(
																						'en-US',
																						{
																							month: 'short',
																							day: 'numeric',
																							hour: '2-digit',
																							minute: '2-digit',
																						}
																					)}
																				</span>
																			</div>
																			<div className='text-lg text-gray-400 truncate max-w-[750px]'>
																				{history.content
																					?.split(
																						'.'
																					)[0]
																					.slice(
																						0,
																						100
																					) +
																					'...'}
																			</div>
																			{history
																				.metadata
																				?.postUrl && (
																				<>
																					<div className='text-sm text-gray-400 line-clamp-1 mt-1'>
																						<span className='font-semibold text-white mr-2'>
																							Published
																							URL:
																						</span>
																						<a
																							href={
																								history
																									.metadata
																									.postUrl
																							}
																							target='_blank'
																							rel='noopener noreferrer'
																							className='text-blue-400 hover:text-blue-300 underline'
																						>
																							{
																								history
																									.metadata
																									.postUrl
																							}
																						</a>
																					</div>
																					{history
																						.metadata
																						?.postId &&
																						machine.platform &&
																						machine.profileKey && (
																							<MiniPostAnalytics
																								contentHistoryId={
																									history._id
																								}
																								postId={
																									history
																										.metadata
																										.postId
																								}
																								platform={
																									machine.platform
																								}
																								profileKey={
																									machine.profileKey
																								}
																								className='mt-1'
																							/>
																						)}
																				</>
																			)}
																		</div>
																	)
																)}
																{getMachineHistory(
																	machine._id
																).length ===
																	0 && (
																	<p className='text-sm text-gray-400 italic p-3'>
																		No
																		content
																		published
																		yet
																	</p>
																)}
															</div>
														</CardContent>
													</Card>
												))}
										</div>
									</div>
								)}

								{/* LinkedIn Machines */}
								{sortedMachines.filter(
									(m) => m.platform === 'linkedin'
								).length > 0 && (
									<div className='space-y-4'>
										<h3 className='text-3xl font-bold text-indigo-500'>
											💼 LINKEDIN
										</h3>
										<div className='grid grid-cols-1 gap-4'>
											{sortedMachines
												.filter(
													(machine) =>
														machine.platform ===
														'linkedin'
												)
												.map((machine) => (
													<Card
														key={machine._id}
														className='bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors'
														onClick={() =>
															navigate(
																`/admin/content-machine/${machine._id}`
															)
														}
														onMouseEnter={() =>
															setHoveredCard(
																machine._id
															)
														}
														onMouseLeave={() =>
															setHoveredCard(null)
														}
													>
														<CardHeader className='pb-4'>
															<div className='flex flex-col gap-2'>
																<div className='flex items-center gap-2'>
																	<span className='text-xl px-6 py-3 bg-gradient-to-b from-blue-400 to-blue-600 text-white rounded-full font-bold border border-blue-300 flex items-center gap-2'>
																		<span className='text-2xl -mt-1'>
																			<LinkedInIcon
																				isHovered={
																					hoveredCard ===
																					machine._id
																				}
																			/>
																		</span>
																		Content
																		Machine
																	</span>
																</div>
																<CardTitle className='text-lg whitespace-nowrap mt-2'>
																	{
																		machine.name
																	}
																</CardTitle>
															</div>
														</CardHeader>
														<CardContent>
															<div className='space-y-4'>
																{getMachineHistory(
																	machine._id
																).map(
																	(
																		history
																	) => (
																		<div
																			key={
																				history._id
																			}
																			className='flex flex-col gap-4 p-6 rounded-lg mb-6 bg-white/5 backdrop-blur-sm border border-white/10 shadow-xl'
																		>
																			<div className='flex items-center gap-2 text-lg text-gray-400'>
																				{history
																					.metadata
																					?.postUrl && (
																					<Badge
																						variant='outline'
																						className='bg-gradient-to-b from-pink-400 to-pink-600 text-white px-3 py-1'
																					>
																						🚀
																						Published
																					</Badge>
																				)}
																				<BrandVoicePill
																					brandVoice={
																						history.brandVoice
																					}
																				/>
																				<span className='text-gray-400'>
																					{new Date(
																						history.createdAt
																					).toLocaleDateString(
																						'en-US',
																						{
																							month: 'short',
																							day: 'numeric',
																							hour: '2-digit',
																							minute: '2-digit',
																						}
																					)}
																				</span>
																			</div>
																			<div className='text-lg text-gray-400 truncate max-w-[750px]'>
																				{history.content?.split(
																					'.'
																				)[0] +
																					'.'}
																			</div>
																			{history
																				.metadata
																				?.postUrl && (
																				<>
																					<div className='text-sm text-gray-400 line-clamp-1 mt-1'>
																						<span className='font-semibold text-white mr-2'>
																							Published
																							URL:
																						</span>
																						<a
																							href={
																								history
																									.metadata
																									.postUrl
																							}
																							target='_blank'
																							rel='noopener noreferrer'
																							className='text-blue-400 hover:text-blue-300 underline'
																						>
																							{
																								history
																									.metadata
																									.postUrl
																							}
																						</a>
																					</div>
																					{history
																						.metadata
																						?.postId &&
																						machine.platform &&
																						machine.profileKey && (
																							<MiniPostAnalytics
																								contentHistoryId={
																									history._id
																								}
																								postId={
																									history
																										.metadata
																										.postId
																								}
																								platform={
																									machine.platform
																								}
																								profileKey={
																									machine.profileKey
																								}
																								className='mt-1'
																							/>
																						)}
																				</>
																			)}
																		</div>
																	)
																)}
																{getMachineHistory(
																	machine._id
																).length ===
																	0 && (
																	<p className='text-sm text-gray-400 italic p-3'>
																		No
																		content
																		published
																		yet
																	</p>
																)}
															</div>
														</CardContent>
													</Card>
												))}
										</div>
									</div>
								)}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Create Content Machine Section */}
				<Card className='glow-card'>
					<CardHeader>
						<CardTitle className='text-2xl font-bold text-pink-500'>
							Create New Content Machine
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-6'>
							<div className='mb-6'>
								<label className='block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300'>
									Platform
								</label>
								<SlideSelector
									value={selectedPlatform}
									onChange={(value) =>
										setSelectedPlatform(value)
									}
									options={[
										{
											value: 'twitterx',
											label: 'X',
											icon: '𝕏',
											description:
												'X Content Machine',
											className:
												'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
										},
										{
											value: 'linkedin',
											label: 'LinkedIn',
											icon: '💼',
											description:
												'LinkedIn Content Machine',
											className:
												'bg-gradient-to-b from-indigo-300 via-indigo-500 to-indigo-700',
										},
									]}
									className='w-full'
									activeColor={
										selectedPlatform === 'twitterx'
											? 'purple'
											: 'indigo'
									}
								/>
							</div>

							<div className='space-y-4'>
								<Input
									placeholder='Enter Content Machine name'
									value={newMachineName}
									onChange={(e) =>
										setNewMachineName(e.target.value)
									}
								/>
								<div className='space-y-2'>
									<label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
										Buzz Agent
									</label>
									<SearchableSelect
										options={buzzAgentOptions}
										value={selectedBuzzAgentId?.toString() || ''}
										onValueChange={(value) =>
											setSelectedBuzzAgentId(
												value as Id<'buzzAgents'>
											)
										}
										placeholder='Select a Buzz Agent'
									/>
									<p className='text-sm text-gray-500'>
										{isBuzzAgentsLoading
											? 'Loading Buzz Agents...'
											: hasActiveBuzzAgents
												? 'The selected Buzz Agent supplies current topics when The Buzz is enabled.'
												: 'No active Buzz Agent is available yet. Refresh Buzz Agents before creating a new Content Machine.'}
									</p>
									{!isBuzzAgentsLoading && !hasActiveBuzzAgents && (
										<Button
											type='button'
											variant='outline'
											onClick={() => navigate('/admin/buzz')}
											className='w-full'
										>
											Open Buzz Agents
										</Button>
									)}
								</div>
								<Button
									onClick={() =>
										handleCreateMachine(selectedPlatform)
									}
									disabled={!canCreateContentMachine}
									className={cn(
										'w-full',
										selectedPlatform === 'twitterx'
											? 'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800'
											: 'bg-gradient-to-b from-indigo-300 via-indigo-500 to-indigo-700',
										'text-white hover:opacity-90'
									)}
								>
									Create Content Machine
								</Button>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	};

	const handleCreateMachine = async (platform: string) => {
		try {
			setIsCreating(true);
			if (!user?.id) return;
			if (!newMachineName.trim()) {
				throw new Error('Content Machine name is required');
			}
			if (!selectedBuzzAgentId) {
				throw new Error(
					'Select an active Buzz Agent before creating a Content Machine'
				);
			}

			// Count existing machines for the user
			const userMachines =
				contentMachines?.filter(
					(m: ContentMachine) =>
						m.userId?.toString() === user.id?.toString()
				) || [];
			if (userMachines.length >= 10) {
				throw new Error(
					'Maximum Content Machines Reached! You can only create up to 10 Content Machines 🚫'
				);
			}

			const machineId = await createContentMachineMutation({
				name: newMachineName,
				platform: platform.toLowerCase(),
				userId: user.id as unknown as Id<'users'>,
				buzzAgentId: selectedBuzzAgentId || undefined,
				agentType: selectedBuzzAgentId ? 'buzz' : undefined,
				agentId: selectedBuzzAgentId
					? selectedBuzzAgentId.toString()
					: undefined,
			});

			// Set the default brand voice to Prometheus
			await updateBrandVoiceMutation({
				contentMachineId: machineId,
				brandVoice: {
					id: 'prometheus',
				},
			});

			setNewMachineName('');
			setSelectedBuzzAgentId(defaultBuzzAgentId);
			triggerConfetti();
			toast({
				title: '✨ Content Machine Created! 🎉',
				description: `New ${platform} Content Machine created successfully! 🚀`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: '❌ Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to create Content Machine',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsCreating(false);
		}
	};

	// Show loading state if authentication is still loading
	if (authLoading || !user?.id || !user?.initialApiKey) {
		const randomMessage =
			LOADER_MESSAGES[Math.floor(Math.random() * LOADER_MESSAGES.length)];
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader className="py-12" message={randomMessage} />
			</div>
		);
	}

	return (
		<div className='container mx-auto p-6 pb-80'>
			<div className='flex justify-between items-center mb-6'>
				<div>
					<h1 className='text-3xl font-bold py-4'>Content Machine</h1>
					<p className='text-lg text-gray-500 -mt-2 mb-4'>
						Create and manage your content machines
					</p>
				</div>
			</div>
			{renderMachinesList()}
			<Confetti />
		</div>
	);
}
